import React, { useEffect, useRef, useState } from "react";

/**
 * VideoCard — vertical 9:16 video card with poster + autoplay-on-scroll.
 * Auto-plays when ≥50% visible, pauses when out of view.
 */
export default function VideoCard({
  poster,
  src,
  label,
  title,
  author,
  duration = "0:47",
  sticker,
  stickerCls = "sticker-lime",
}) {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            el.play()
              .then(() => setPlaying(true))
              .catch(() => {});
          } else {
            el.pause();
            setPlaying(false);
          }
        });
      },
      { threshold: 0.5 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const togglePlay = () => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <div className="group relative aspect-[9/16] rounded-[28px] overflow-hidden bg-[#0e0e10] cursor-pointer shadow-xl hover:shadow-2xl transition-all">
      <video
        ref={ref}
        poster={poster}
        preload="metadata"
        playsInline
        loop
        muted
        onClick={togglePlay}
        onEnded={() => setPlaying(false)}
        className="absolute inset-0 w-full h-full object-cover"
      >
        {src && <source src={src} type="video/mp4" />}
      </video>

      {/* Gradient overlay for legibility */}
      <div
        className={`absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/40 pointer-events-none transition-opacity ${
          playing ? "opacity-0" : "opacity-100"
        }`}
      />

      {/* Sticker */}
      {sticker && (
        <span
          className={`sticker ${stickerCls} absolute top-4 left-4 text-[11px] z-10`}
        >
          {sticker}
        </span>
      )}

      {/* Duration pill */}
      <span className="absolute top-4 right-4 bg-black/60 backdrop-blur text-white text-[10px] font-bold tracking-wider px-2.5 py-1 rounded-full z-10">
        ▶ {duration}
      </span>

      {/* Play button (center) */}
      {!playing && (
        <button
          onClick={togglePlay}
          aria-label="Play video"
          className="absolute inset-0 flex items-center justify-center z-10"
        >
          <span className="w-16 h-16 md:w-20 md:h-20 rounded-full bg-white/95 flex items-center justify-center shadow-2xl group-hover:scale-110 transition-transform">
            <span className="ml-1.5 text-2xl md:text-3xl text-[#0e0e10]">▶</span>
          </span>
        </button>
      )}

      {/* Bottom text */}
      <div className="absolute left-0 right-0 bottom-0 p-5 z-10 text-white pointer-events-none">
        {label && (
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold opacity-80">
            {label}
          </div>
        )}
        <div className="mt-1 font-extrabold text-[16px] md:text-[17px] leading-tight">
          {title}
        </div>
        {author && (
          <div className="text-[12px] opacity-80 mt-1">{author}</div>
        )}
      </div>
    </div>
  );
}
